import { DOCUMENT } from '@angular/common';
import { Inject, Injectable, signal } from '@angular/core';

export type AdminTheme = 'dark' | 'light';

/** Clave de la preferencia. Se conserva el nombre histórico para que una
 *  elección guardada antes de esta versión (p. ej. 'light') se respete. */
export const THEME_STORAGE_KEY = 'tusala_admin_theme';
/** Tema cuando el usuario todavía no eligió uno: claro. */
export const DEFAULT_THEME: AdminTheme = 'light';

/** Duración de la revelación. */
const REVEAL_DURATION_MS = 650;
/** Curva exponencial: arranque decidido y frenado largo (ritmo de producto). */
const REVEAL_EASING = 'cubic-bezier(.7, 0, .18, 1)';
/** Radio interior de la forma de aurora respecto de su caja (ver styles.css). */
const AURORA_INNER_RATIO = 0.8;
/** Tamaño de dibujo de la luz de aurora (se amplía al animar). */
const AURORA_RING_BASE_PX = 1400;
/** Fundido de respaldo para navegadores sin View Transitions. */
const FALLBACK_FADE_MS = 320;

/**
 * Tema del panel.
 *
 * Persistencia: la elección se guarda en localStorage de ESTE navegador (no
 * en el backend, que no tiene un campo de preferencias para el panel). Por
 * eso se conserva al recargar y al cerrar/abrir sesión en el mismo
 * navegador, pero no viaja entre dispositivos y es compartida por las
 * cuentas que usan el mismo navegador. index.html aplica la misma regla
 * antes de que cargue Angular para evitar parpadeos.
 *
 * Transición «aurora» (solo al cambiar manualmente): el tema nuevo se abre
 * desde el control pulsado con una máscara de contorno orgánico (pliegues
 * irregulares, borde difuminado) cuyo frente es una luz de aurora en los
 * colores de marca (cian, azul, índigo); la vista anterior retrocede
 * levemente (escala y brillo) mientras la nueva se asienta. Usa View Transitions: se animan
 * instantáneas, nunca los elementos reales (ver styles.css, «Cambio de
 * tema»). Sin soporte, un fundido global breve; con prefers-reduced-motion
 * el cambio es inmediato.
 */
@Injectable({ providedIn: 'root' })
export class ThemeService {
  readonly theme = signal<AdminTheme>(this.readTheme());

  private activeTransition: { skipTransition(): void } | null = null;
  private ring: HTMLElement | null = null;
  private fallbackOverlay: HTMLElement | null = null;

  constructor(@Inject(DOCUMENT) private readonly document: Document) { this.apply(this.theme()); }

  /** Alterna el tema. `origin` es el evento o el elemento que lo inició; la
   *  revelación nace en su centro (si falta, en el centro de la pantalla). */
  toggle(origin?: Event | Element | null): void {
    const next: AdminTheme = this.theme() === 'dark' ? 'light' : 'dark';
    this.setTheme(next, origin ?? null);
  }

  /** Fija y guarda el tema. Con `origin` (cambio manual) se anima. */
  setTheme(theme: AdminTheme, origin?: Event | Element | null): void {
    const changed = theme !== this.theme();
    this.theme.set(theme);
    try { localStorage.setItem(THEME_STORAGE_KEY, theme); } catch {}
    if (changed && origin !== undefined) this.applyAnimated(theme, origin);
    else this.apply(theme);
  }

  /** El acceso conserva deliberadamente su composición oscura sobre foto.
   *  No modifica la preferencia: al entrar al panel se restaura el tema
   *  elegido por el usuario (claro cuando todavía no existe elección). */
  applyForRoute(isLogin: boolean): void {
    this.apply(isLogin ? 'dark' : this.theme());
  }

  private apply(theme: AdminTheme): void {
    const root = this.document.documentElement;
    root.classList.toggle('tusala-light-theme', theme === 'light');
    root.classList.toggle('tusala-dark-theme', theme === 'dark');
    root.style.colorScheme = theme;
  }

  private applyAnimated(theme: AdminTheme, origin: Event | Element | null): void {
    // Un cambio rápido y repetido no encola animaciones: se termina la
    // anterior al instante y se aplica el estado nuevo.
    this.activeTransition?.skipTransition();
    this.cleanupReveal();

    const win = this.document.defaultView;
    const reduceMotion = !!win?.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
    const doc = this.document as Document & {
      startViewTransition?: (update: () => void) => { ready: Promise<void>; finished: Promise<void>; skipTransition(): void };
    };
    if (!win || reduceMotion) { this.apply(theme); return; }
    if (typeof doc.startViewTransition !== 'function') { this.fallbackFade(theme); return; }

    const root = this.document.documentElement;
    const { x, y } = this.originPoint(origin, win);
    // La forma de aurora tiene radio interior ~0,68 de su caja: el tamaño
    // final garantiza que su borde ondulado salga de la esquina más lejana.
    const farthest = Math.hypot(Math.max(x, win.innerWidth - x), Math.max(y, win.innerHeight - y));
    const size = Math.ceil((farthest * 2) / AURORA_INNER_RATIO);
    root.style.setProperty('--ts-reveal-x', `${Math.round(x)}px`);
    root.style.setProperty('--ts-reveal-y', `${Math.round(y)}px`);
    root.style.setProperty('--ts-reveal-max', `${size}px`);
    root.style.setProperty('--ts-reveal-duration', `${REVEAL_DURATION_MS}ms`);
    root.style.setProperty('--ts-reveal-easing', REVEAL_EASING);
    root.dataset['tsReveal'] = theme;
    root.classList.add('ts-theme-transition');
    const transition = doc.startViewTransition(() => {
      this.apply(theme);
      // El borde de luz solo existe en la instantánea nueva; crece con la máscara.
      this.createRing(x, y, size);
    });
    this.activeTransition = transition;
    transition.ready.catch(() => {});
    transition.finished.finally(() => {
      if (this.activeTransition !== transition) return;
      this.activeTransition = null;
      this.cleanupReveal();
    });
  }

  /** Borde de aurora (aria-hidden, sin interacción) del tamaño final de la
   *  revelación; la transición lo escala desde el botón al mismo ritmo que
   *  la máscara, de modo que la luz siempre coincide con el frente. */
  private createRing(x: number, y: number, size: number): void {
    const ring = this.document.createElement('div');
    ring.className = 'ts-reveal-ring';
    ring.setAttribute('aria-hidden', 'true');
    // Se dibuja a un tamaño acotado y se amplía en la animación hasta el
    // tamaño final: una textura de pantalla completa x2 costaba fotogramas.
    const base = Math.min(size, AURORA_RING_BASE_PX);
    const half = base / 2;
    ring.style.cssText = `left:${Math.round(x - half)}px;top:${Math.round(y - half)}px;width:${base}px;height:${base}px`;
    this.document.documentElement.style.setProperty('--ts-ring-scale', String(size / base));
    this.document.body.appendChild(ring);
    this.ring = ring;
  }

  private cleanupReveal(): void {
    this.ring?.remove();
    this.ring = null;
    this.fallbackOverlay?.remove();
    this.fallbackOverlay = null;
    const root = this.document.documentElement;
    root.classList.remove('ts-theme-transition');
    delete root.dataset['tsReveal'];
    for (const prop of ['--ts-reveal-x', '--ts-reveal-y', '--ts-reveal-max', '--ts-reveal-duration', '--ts-reveal-easing', '--ts-ring-scale']) root.style.removeProperty(prop);
  }

  /** Sin View Transitions: velo del color del tema anterior que se
   *  desvanece sobre el nuevo. Un solo elemento, sin animar la página. */
  private fallbackFade(theme: AdminTheme): void {
    const previousBg = getComputedStyle(this.document.body).backgroundColor;
    this.apply(theme);
    const veil = this.document.createElement('div');
    veil.setAttribute('aria-hidden', 'true');
    veil.style.cssText = `position:fixed;inset:0;z-index:2147483647;pointer-events:none;background:${previousBg};opacity:.55;transition:opacity ${FALLBACK_FADE_MS}ms ${REVEAL_EASING}`;
    this.document.body.appendChild(veil);
    this.fallbackOverlay = veil;
    requestAnimationFrame(() => { veil.style.opacity = '0'; });
    setTimeout(() => { if (this.fallbackOverlay === veil) { veil.remove(); this.fallbackOverlay = null; } }, FALLBACK_FADE_MS + 60);
  }

  private originPoint(origin: Event | Element | null, win: Window): { x: number; y: number } {
    const element = origin instanceof Element ? origin
      : origin && origin.currentTarget instanceof Element ? origin.currentTarget : null;
    if (element) {
      const rect = element.getBoundingClientRect();
      if (rect.width || rect.height) return { x: rect.left + rect.width / 2, y: rect.top + rect.height / 2 };
    }
    return { x: win.innerWidth / 2, y: win.innerHeight / 2 };
  }

  private readTheme(): AdminTheme {
    try {
      const stored = localStorage.getItem(THEME_STORAGE_KEY);
      return stored === 'light' || stored === 'dark' ? stored : DEFAULT_THEME;
    } catch { return DEFAULT_THEME; }
  }
}
